// Data Service - Abstracts localStorage and API calls
// Uses localStorage for non-authenticated users, API for authenticated users
// Direct fetch helper — same pattern as the working step goal (bypasses api.ts)
import { getToken } from '../utils/tokenStorage';
import { sendOrQueue } from '../utils/offlineQueue';
import { ensurePlanMigrated } from '../utils/ensurePlanMigrated';
import { replaceExerciseHistory as writeExerciseHistoryCache, clearExerciseHistoryCache } from '../utils/exerciseHistoryCache';
import { WORKOUT_HISTORY_DAYS } from '../utils/historyWindow';

const MUTATING = /^(POST|PUT|PATCH|DELETE)$/i;

export async function apiFetch(endpoint: string, options?: RequestInit): Promise<any> {
  const token = getToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const init: RequestInit = { ...options, headers: { ...headers, ...options?.headers } };
  const method = String(options?.method || 'GET');

  // iter354 — writes made with no signal used to vanish: the local copy was
  // saved but the server never heard about it. Mutating calls are now parked
  // in IndexedDB and replayed in order once the connection is back.
  if (MUTATING.test(method)) {
    const res = await sendOrQueue(endpoint, { ...init, method }, {
      label: endpoint.replace('/api/', ''),
      // Full-state PUTs are idempotent, so a burst of offline edits collapses
      // to the newest payload instead of replaying every intermediate state.
      dedupeKey: method.toUpperCase() === 'PUT' ? `PUT:${endpoint}` : undefined,
    });
    if (!res) return { queued: true };
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    if (res.status === 204) return {};
    const body = await res.text();
    return body ? JSON.parse(body) : {};
  }

  const response = await fetch(endpoint, init);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  if (response.status === 204) return {};
  const text = await response.text();
  if (!text) return {};
  return JSON.parse(text);
}

// Notify the SyncProvider that local data has changed. SyncContext listens for
// this and triggers a silent S3 backup (throttled to 6h inside). Safe to call
// often — it's a no-op when offline or recently backed up.
export function notifyDataChanged(): void {
  try {
    window.dispatchEvent(new CustomEvent('dataChanged'));
  } catch { /* noop */ }
}

const STORAGE_KEYS = {
  ACTIVE_PLAN: 'gym_notebook_active_plan',
  TRAINING_HISTORY: 'gym_notebook_training_history',
  EXERCISE_HISTORY: 'gym_notebook_exercise_history',
  FAVORITES: 'gym_notebook_favorites',
  BODY_MEASUREMENTS: 'gym_notebook_body_measurements_history',
  PROGRESS_PHOTOS: 'gym_notebook_progress_photos',
  THEME: 'gym_notebook_theme',
  FOCUS_MODE: 'gym_notebook_focus_mode',
} as const;

// Type definitions
interface ExerciseSet {
  done?: boolean;
  skipped?: boolean;
  reps?: string | number;
  weight?: string | number;
}

interface Exercise {
  name?: string;
  sets?: ExerciseSet[];
  [key: string]: unknown;
}

interface TrainingPlan {
  id?: string;
  plan_id?: string;
  createdAt?: string;
  created_at?: string;
  updatedAt?: number;
  workoutDuration?: number;
  trainingWeeks?: number;
  hasDeloadWeek?: boolean;
  deloadWeek?: number;
  trainingDays?: string[];
  hiitWalkingDays?: string[];
  weightUnit?: string;
  bodyweight?: string;
  bodyMeasurements?: Record<string, unknown>;
  measurementUnit?: string;
  selectedMuscles?: string[];
  dayMuscleAssignments?: Record<string, string[]>;
  dayExercises?: Record<string, Exercise[]>;
  deloadExercises?: Record<string, Exercise[]>;
  currentWeek?: number;
  currentDayIndex?: number;
  name?: string;
  _preDeloadDayExercises?: Record<string, Exercise[]>;
  manualDeloadWeeks?: number[];
  manualDeloadReasons?: Record<string, string>;
}

interface BackendPlan {
  plan_id?: string;
  id?: string;
  created_at?: string;
  createdAt?: string;
  workout_duration?: number;
  workoutDuration?: number;
  training_weeks?: number;
  trainingWeeks?: number;
  has_deload_week?: boolean;
  hasDeloadWeek?: boolean;
  deload_week?: number;
  deloadWeek?: number;
  training_days?: string[];
  trainingDays?: string[];
  hiit_walking_days?: string[];
  hiitWalkingDays?: string[];
  weight_unit?: string;
  weightUnit?: string;
  bodyweight?: string;
  body_measurements?: Record<string, unknown>;
  bodyMeasurements?: Record<string, unknown>;
  measurement_unit?: string;
  measurementUnit?: string;
  selected_muscles?: string[];
  selectedMuscles?: string[];
  day_muscle_assignments?: Record<string, string[]>;
  dayMuscleAssignments?: Record<string, string[]>;
  day_exercises?: Record<string, Exercise[]>;
  dayExercises?: Record<string, Exercise[]>;
  deload_exercises?: Record<string, Exercise[]>;
  deloadExercises?: Record<string, Exercise[]>;
  current_week?: number;
  currentWeek?: number;
  current_day_index?: number;
  currentDayIndex?: number;
}

interface TrainingHistoryEntry {
  id?: string;
  entry_id?: string;
  date?: string;
  dayKey?: string;
  day_key?: string;
  day?: number;
  week?: number;
  planId?: string | null;
  skipped?: boolean;
  isDeload?: boolean;
  exercises?: Exercise[];
  durationMinutes?: number;
  notes?: string;
  completedAt?: string;
}

interface Favorite {
  id?: number | string;
  favorite_id?: string;
  name?: string;
  workoutDuration?: number;
  workout_duration?: number;
  trainingDays?: string[];
  training_days?: string[];
  hiitWalkingDays?: string[];
  hiit_walking_days?: string[];
  weightUnit?: string;
  weight_unit?: string;
  bodyweight?: string;
  bodyMeasurements?: Record<string, unknown>;
  body_measurements?: Record<string, unknown>;
  measurementUnit?: string;
  measurement_unit?: string;
  selectedMuscles?: string[];
  selected_muscles?: string[];
  dayMuscleAssignments?: Record<string, string[]>;
  day_muscle_assignments?: Record<string, string[]>;
  dayExercises?: Record<string, Exercise[]>;
  day_exercises?: Record<string, Exercise[]>;
  createdAt?: string;
  created_at?: string;
}

interface BodyMeasurementEntry {
  date: string;
  bodyweight?: string;
  weightUnit?: string;
  measurements?: Record<string, unknown>;
  measurementUnit?: string;
}

interface ProgressPhoto {
  id?: string;
  date?: string;
  [key: string]: unknown;
}

interface ExerciseHistoryEntry {
  date?: string;
  [key: string]: unknown;
}

type ExerciseHistoryMap = Record<string, ExerciseHistoryEntry[]>;

interface SyncData {
  data_type: string;
  training_plan: TrainingPlan | null;
  training_history: TrainingHistoryEntry[];
  exercise_history: ExerciseHistoryMap;
  favorites: Favorite[];
  body_measurements: BodyMeasurementEntry[];
  progress_photos: ProgressPhoto[];
  settings: {
    theme: string;
  };
  // Extended data types
  custom_exercises?: unknown[];
  saved_meals?: unknown[];
  nutrition_days?: unknown[];
  walking_sessions?: unknown[];
  custom_products?: unknown[];
}

interface SyncResult {
  synced: boolean;
  response?: unknown;
  error?: string;
  reason?: string;
}

class DataService {
  isAuthenticated: boolean = false;
  syncEnabled: boolean = false;

  setAuthStatus(isAuthenticated: boolean, syncEnabled: boolean = false): void {
    this.isAuthenticated = isAuthenticated;
    this.syncEnabled = syncEnabled;
  }

  // ============ TRAINING PLAN ============
  
  // Convert backend snake_case plan to frontend camelCase
  _convertPlanFromBackend(backendPlan: BackendPlan | null): TrainingPlan | null {
    if (!backendPlan) return null;
    
    // Filter out known bad bodyweight values (78kg bug)
    let bodyweight = backendPlan.bodyweight;
    const badWeights = ['78', '78kg', '78 kg'];
    if (bodyweight && (badWeights.includes(bodyweight) || parseFloat(bodyweight) === 78)) {
      console.log('Filtering out bad bodyweight 78 from backend');
      bodyweight = '';
    }
    
    return {
      id: backendPlan.plan_id || backendPlan.id,
      plan_id: backendPlan.plan_id,
      createdAt: backendPlan.created_at || backendPlan.createdAt,
      workoutDuration: backendPlan.workout_duration || backendPlan.workoutDuration,
      trainingWeeks: backendPlan.training_weeks || backendPlan.trainingWeeks,
      hasDeloadWeek: backendPlan.has_deload_week || backendPlan.hasDeloadWeek,
      deloadWeek: backendPlan.deload_week || backendPlan.deloadWeek,
      trainingDays: backendPlan.training_days || backendPlan.trainingDays || [],
      hiitWalkingDays: backendPlan.hiit_walking_days || backendPlan.hiitWalkingDays || [],
      weightUnit: backendPlan.weight_unit || backendPlan.weightUnit,
      bodyweight: bodyweight,
      bodyMeasurements: backendPlan.body_measurements || backendPlan.bodyMeasurements || {},
      measurementUnit: backendPlan.measurement_unit || backendPlan.measurementUnit || 'cm',
      selectedMuscles: backendPlan.selected_muscles || backendPlan.selectedMuscles || [],
      dayMuscleAssignments: backendPlan.day_muscle_assignments || backendPlan.dayMuscleAssignments || {},
      dayExercises: backendPlan.dayExercises || backendPlan.day_exercises || {},
      deloadExercises: backendPlan.deload_exercises || backendPlan.deloadExercises,
      currentWeek: backendPlan.current_week || backendPlan.currentWeek || 1,
      currentDayIndex: backendPlan.current_day_index || backendPlan.currentDayIndex || 0,
      _preDeloadDayExercises: (backendPlan as any)._preDeloadDayExercises || undefined,
      manualDeloadWeeks: (backendPlan as any).manualDeloadWeeks || undefined,
      manualDeloadReasons: (backendPlan as any).manualDeloadReasons || undefined,
      // DUP-3 plan-level config — these live in `active_plans` (written via
      // PUT /api/plans/active) but are NOT mirrored to `training_plans`
      // (the legacy collection this method usually receives). Keep them
      // here so a backend doc that happens to carry them (e.g. an active
      // plan returned via a non-standard path) still hydrates the fields
      // instead of dropping them on the floor — the production bug where
      // the DUP-3 `· N-rep · Hypertrophy` chip vanished after every page
      // refresh because the merge spread `...backendPlan` first and
      // never re-added these top-level keys.
      ...(((backendPlan as any).dupWeekdaySchedule) && { dupWeekdaySchedule: (backendPlan as any).dupWeekdaySchedule }),
      ...(((backendPlan as any).dupRepRanges) && { dupRepRanges: (backendPlan as any).dupRepRanges }),
    } as TrainingPlan;
  }

  async getActivePlan(): Promise<TrainingPlan | null> {
    // Get local plan first
    const localSaved = localStorage.getItem(STORAGE_KEYS.ACTIVE_PLAN);
    const localPlan: TrainingPlan | null = localSaved ? JSON.parse(localSaved) : null;
    
    if (this.isAuthenticated && this.syncEnabled) {
      try {
        const response = await apiFetch('/api/data/training-plan');
        
        console.log('[SYNC] Backend response: sync_week=', response.sync_week, 'local_week=', localPlan?.currentWeek, 'has_active_plan=', !!response.active_plan);
        
        // Cross-device sync: only fill in weights for sets that have NO weight at all (undefined).
        // Never overwrite existing local weights — the user's edits are the source of truth.
        // Iter252n — EXCEPTION: if the backend just ran the deload-heal
        // (indicated by a fresh `_healedDeloadCorruptionAt` marker),
        // OVERWRITE local weights on APRE/DUP exercises with the healed
        // values. Local weights in that case are the corrupted 75%
        // ghosts we are trying to fix — user edits are NOT source of
        // truth here. `done` and completed sets stay untouched.
        if (response.active_plan?.dayExercises && localPlan?.dayExercises) {
          const localCreatedTs = new Date(localPlan.createdAt || localPlan.created_at || 0).getTime();
          const remoteCreatedTs = new Date(response.active_plan.createdAt || response.active_plan.created_at || 0).getTime();
          const localIsNewer = localCreatedTs > 0 && remoteCreatedTs > 0 && localCreatedTs > remoteCreatedTs;
          const backendHealMarker = (response.active_plan as any)._healedDeloadCorruptionAt;
          const backendHealMarkerV2 = (response.active_plan as any)._healedDeloadCorruptionAt_v2;
          const backendHealMarkerV3 = (response.active_plan as any)._healedDeloadCorruptionAt_v3;
          const localHealMarker = (localPlan as any)._healedDeloadCorruptionAt;
          const localHealMarkerV2 = (localPlan as any)._healedDeloadCorruptionAt_v2;
          const localHealMarkerV3 = (localPlan as any)._healedDeloadCorruptionAt_v3;
          // Iter252p — heal event detected when any of the version
          // markers (v1/v2/v3) on backend differs from local's copy.
          // v3 fixes stale-cycle rows + APRE-shape mis-detect.
          const justHealed = (!!backendHealMarker && backendHealMarker !== localHealMarker)
            || (!!backendHealMarkerV2 && backendHealMarkerV2 !== localHealMarkerV2)
            || (!!backendHealMarkerV3 && backendHealMarkerV3 !== localHealMarkerV3);

          // Iter252o — CLIENT-SIDE deload-corruption detection.
          // Real-user finding (Verner 2026-08-02): backend `active_plans`
          // was HEALTHY but the phone's localStorage/IndexedDB had the
          // corruption (manualDeloadWeeks=[N] + null manualDeloadReasons
          // + missing _preDeloadDayExercises snapshot, plus APRE/DUP
          // weights cut ~25% by an in-deload write). LocalPlan.updatedAt
          // was newer than backend so the normal merge kept the corrupt
          // local values. We now detect this signature on the CLIENT
          // and force-adopt the backend's healthy APRE/DUP maps + clear
          // the poisoned deload state locally. Symmetric to the backend
          // v1→v2 heal but for the opposite direction (backend clean,
          // client corrupt).
          const _lp: any = localPlan;
          const _rp: any = response.active_plan;
          const localMdw = Array.isArray(_lp.manualDeloadWeeks) ? _lp.manualDeloadWeeks : [];
          const localReasons = _lp.manualDeloadReasons;
          const localSnap = _lp._preDeloadDayExercises;
          const localReasonsComplete = localReasons && typeof localReasons === 'object'
            && localMdw.every((w: any) => String(w) in localReasons || w in localReasons);
          const localSnapPresent = localSnap && typeof localSnap === 'object' && Object.keys(localSnap).length > 0;
          const localHasCorruption = localMdw.length > 0 && (!localReasonsComplete || !localSnapPresent);
          const remoteMdw = Array.isArray(_rp.manualDeloadWeeks) ? _rp.manualDeloadWeeks : [];
          const remoteIsClean = remoteMdw.length === 0;
          const clientSideCorruptionHeal = localHasCorruption && remoteIsClean;
          
          if (!localIsNewer || clientSideCorruptionHeal) {
            const remoteDayEx = response.active_plan.dayExercises;
            let updated = false;
            const forceOverwrite = justHealed || clientSideCorruptionHeal;
            Object.keys(remoteDayEx).forEach(dayKey => {
              if (!localPlan.dayExercises![dayKey]) return;
              remoteDayEx[dayKey].forEach((remoteEx: any, exIdx: number) => {
                const localEx = localPlan.dayExercises![dayKey]?.[exIdx];
                if (!localEx || localEx.name !== remoteEx.name) return;
                const isAutoregulated = !!(remoteEx.apre?.enabled || remoteEx.dup?.enabled);
                remoteEx.sets?.forEach((remoteSet: any, sIdx: number) => {
                  const localSet = localEx.sets?.[sIdx];
                  if (!localSet) return;
                  const healOverride = forceOverwrite && isAutoregulated && !localSet.done;
                  if (healOverride) {
                    // Force-adopt healed weight even when local has one.
                    if (remoteSet.weight !== undefined) {
                      localSet.weight = remoteSet.weight;
                      localSet.reps = remoteSet.reps;
                      updated = true;
                    }
                    return;
                  }
                  // Only import if local set has NO weight defined at all (truly empty slot)
                  if (localSet.weight === undefined && remoteSet.weight) {
                    localSet.weight = remoteSet.weight;
                    localSet.reps = remoteSet.reps;
                    updated = true;
                  }
                });
              });
            });
            if (updated) {
              console.log('[SYNC]',
                clientSideCorruptionHeal ? 'Client-side corruption heal — adopted backend healthy APRE/DUP'
                : justHealed ? 'Applied deload heal from backend'
                : 'Filled empty weights from cross-device active_plan');
            }
            if (forceOverwrite) {
              // Also propagate the top-level heal-related fields so we
              // never re-run against the same heal event.
              (localPlan as any)._healedDeloadCorruptionAt = backendHealMarker || (localPlan as any)._healedDeloadCorruptionAt;
              (localPlan as any)._healedDeloadCorruptionAt_v2 = backendHealMarkerV2 || (localPlan as any)._healedDeloadCorruptionAt_v2;
              (localPlan as any)._healedDeloadCorruptionAt_v3 = backendHealMarkerV3 || (localPlan as any)._healedDeloadCorruptionAt_v3;
              (localPlan as any).apreExerciseRm = (response.active_plan as any).apreExerciseRm;
              (localPlan as any).dupExerciseWeights = (response.active_plan as any).dupExerciseWeights;
              // For client-side corruption, we ALSO clear the poisoned
              // deload-state fields locally so the next `applyApreToExercise`
              // / `handleDupSetDone` no longer misreads current week as
              // a deload and reduces weights again.
              (localPlan as any).manualDeloadWeeks = (response.active_plan as any).manualDeloadWeeks || [];
              (localPlan as any).manualDeloadReasons = (response.active_plan as any).manualDeloadReasons || {};
              if (clientSideCorruptionHeal) {
                delete (localPlan as any)._preDeloadDayExercises;
              }
              localPlan.updatedAt = (response.active_plan as any).updatedAt || Date.now();
            }
          }
        }
        
        // Check if backend has a higher week number (from sync_data/active_plans)
        if (response.sync_week && localPlan) {
          const localWeek = localPlan.currentWeek || 1;
          if (response.sync_week > localWeek) {
            console.log('[SYNC] Advancing week from', localWeek, 'to', response.sync_week);
            localPlan.currentWeek = response.sync_week;
            localPlan.currentDayIndex = 0;
            // Reset done status for new week
            //
            // Iter252be (Feb 2026): REMOVED the halfReps / usePartials /
            // drops stripping that used to live here. Reason: this branch
            // fires whenever `sync_week > localWeek`, but sync_week comes
            // from `user_sync_data.currentWeek` which can end up ahead of
            // the local plan for reasons OTHER than a legitimate roll-
            // over (cross-device sync race, a heartbeat write, an admin
            // edit, etc.). Stripping session modifiers on those paths
            // silently deletes the user's current-week half-reps and
            // dropsets — exactly the "workouts missing half reps and
            // dropsets after refresh" bug reported by Verner on 12.08.
            // `done` markers are still reset because they clearly belong
            // to the previous week's completions; halfReps / drops are
            // programming choices that should survive.
            if (localPlan.dayExercises) {
              Object.keys(localPlan.dayExercises).forEach(dayKey => {
                localPlan.dayExercises![dayKey].forEach(ex => {
                  (ex as any).skipped = false;
                  if (ex.sets) {
                    ex.sets.forEach(s => {
                      s.done = false;
                      (s as any).skipped = false;
                    });
                  }
                });
              });
            }
            // Don't overwrite localStorage here — let usePlanLoader handle storage
            return localPlan;
          }
        }
        
        if (response.plan) {
          // Convert snake_case from backend to camelCase for frontend
          const backendPlan = this._convertPlanFromBackend(response.plan);
          
          // IMPORTANT: Merge backend plan with local plan to preserve local changes
          if (localPlan && backendPlan && localPlan.plan_id === backendPlan.plan_id) {
            // If local plan was restarted/copied more recently, prefer local over merge
            // This prevents stale backend training_plans entries from corrupting a fresh plan restart
            const localCreated = new Date(localPlan.createdAt || 0).getTime();
            const backendCreated = new Date(backendPlan.createdAt || 0).getTime();
            if (localCreated > backendCreated) {
              console.log('[SYNC] Local plan is newer (restarted/copied) — skipping merge with stale backend');
              return localPlan;
            }
            
            // Same plan, same age - merge dayExercises carefully
            // Preserve local updatedAt so the freshness check in usePlanLoader works
            const mergedPlan: TrainingPlan = {
              ...backendPlan,
              // Iter326 — `backendPlan` is the `training_plans` mirror, which
              // historically lagged behind plan-shape edits (duration / deload
              // / training days live in `active_plans`). Trusting its
              // `workoutDuration` is what produced the "finished mid-cycle"
              // screen, so prefer the active_plan doc, then the local plan.
              ...(() => {
                const ap: any = response.active_plan || {};
                const pick = (key: keyof TrainingPlan) => {
                  const v = (ap as any)[key] ?? (localPlan as any)[key];
                  return v === undefined || v === null ? {} : { [key]: v };
                };
                return {
                  ...pick('workoutDuration' as keyof TrainingPlan),
                  ...pick('trainingWeeks' as keyof TrainingPlan),
                  ...pick('hasDeloadWeek' as keyof TrainingPlan),
                  ...pick('deloadWeek' as keyof TrainingPlan),
                } as any;
              })(),
              updatedAt: localPlan.updatedAt,
              currentWeek: Math.max(localPlan.currentWeek || 1, backendPlan.currentWeek || 1),
              currentDayIndex: localPlan.currentDayIndex ?? backendPlan.currentDayIndex ?? 0,
              dayExercises: this._mergeDayExercises(localPlan.dayExercises, backendPlan.dayExercises),
              // Preserve deload state from local plan (not stored in training_plans collection)
              _preDeloadDayExercises: localPlan._preDeloadDayExercises,
              manualDeloadWeeks: (localPlan as any).manualDeloadWeeks ?? (backendPlan as any).manualDeloadWeeks,
              manualDeloadReasons: (localPlan as any).manualDeloadReasons ?? (backendPlan as any).manualDeloadReasons,
              // Preserve DUP-3 plan-level config — `dupWeekdaySchedule`
              // and `dupRepRanges` live in `active_plans` only (the
              // `training_plans` collection — i.e. `backendPlan` — does
              // not carry them). Prefer the freshest source: the
              // `active_plan` doc returned alongside `plan`, else the
              // local copy that was just synced from there.
              ...((response.active_plan as any)?.dupWeekdaySchedule || (localPlan as any).dupWeekdaySchedule
                ? { dupWeekdaySchedule: (response.active_plan as any)?.dupWeekdaySchedule || (localPlan as any).dupWeekdaySchedule } as any
                : {}),
              ...((response.active_plan as any)?.dupRepRanges || (localPlan as any).dupRepRanges
                ? { dupRepRanges: (response.active_plan as any)?.dupRepRanges || (localPlan as any).dupRepRanges } as any
                : {}),
              // Plan-level RM canonical maps (Feb 2026). Like
              // `dupWeekdaySchedule` these live in `active_plans` only
              // — the `training_plans` collection does not carry them.
              ...((response.active_plan as any)?.dupExerciseRm12 || (localPlan as any).dupExerciseRm12
                ? { dupExerciseRm12: (response.active_plan as any)?.dupExerciseRm12 || (localPlan as any).dupExerciseRm12 } as any
                : {}),
              // Iter252n — v3 canonical DUP working-weight store must
              // be layered too. Without this, the deload heal writes
              // `active_plan.dupExerciseWeights` but the same-plan
              // merge path drops it silently — user saw "APRE OK, DUP
              // not fixed" because APRE (below) IS layered but DUP v3
              // wasn't. `training_plans` doesn't carry `dupExerciseWeights`
              // so we prefer `active_plan`'s copy over local ONLY when
              // active_plan carries a fresher heal marker; otherwise
              // prefer local so in-session user updates (setDupWeight
              // called by handleDupSetDone) aren't overwritten by a
              // stale backend snapshot.
              ...((() => {
                const remoteWeights = (response.active_plan as any)?.dupExerciseWeights;
                const localWeights = (localPlan as any).dupExerciseWeights;
                const remoteHealV2 = (response.active_plan as any)?._healedDeloadCorruptionAt_v2;
                const localHealV2 = (localPlan as any)?._healedDeloadCorruptionAt_v2;
                const remoteJustHealed = !!remoteHealV2 && remoteHealV2 !== localHealV2;
                if (remoteJustHealed && remoteWeights) return { dupExerciseWeights: remoteWeights } as any;
                if (localWeights) return { dupExerciseWeights: localWeights } as any;
                if (remoteWeights) return { dupExerciseWeights: remoteWeights } as any;
                return {};
              })()),
              ...((response.active_plan as any)?.apreExerciseRm || (localPlan as any).apreExerciseRm
                ? { apreExerciseRm: (response.active_plan as any)?.apreExerciseRm || (localPlan as any).apreExerciseRm } as any
                : {}),
            };
            // Don't overwrite localStorage here — let usePlanLoader handle storage
            // to avoid race conditions with concurrent user edits
            return mergedPlan;
          }
          
          // Different plan - check if local is newer (e.g., user restarted with same template)
          if (localPlan && backendPlan) {
            const localCreated = new Date(localPlan.createdAt || 0).getTime();
            const backendCreated = new Date(backendPlan.createdAt || 0).getTime();
            if (localCreated > backendCreated) {
              console.log('[SYNC] Local plan is newer than different backend plan — keeping local');
              return localPlan;
            }
          }
          
          // No local plan or backend is newer - use backend
          // Don't overwrite localStorage here — let usePlanLoader handle storage
          //
          // Layer in DUP-3 plan-level config from the active_plan doc if
          // present — the `training_plans` collection (i.e. `backendPlan`)
          // never carries `dupWeekdaySchedule` / `dupRepRanges`, but the
          // `active_plans` collection (returned alongside `plan` in
          // `response.active_plan`) does. Without this layering, the
          // DUP-3 schedule is silently dropped on first load and the
          // exercise card's `· N-rep · Hypertrophy` chip never appears.
          if (backendPlan && response.active_plan) {
            const ap: any = response.active_plan;
            if (ap.dupWeekdaySchedule) (backendPlan as any).dupWeekdaySchedule = ap.dupWeekdaySchedule;
            if (ap.dupRepRanges) (backendPlan as any).dupRepRanges = ap.dupRepRanges;
            // Plan-level RM canonical maps (Feb 2026) live in
            // active_plans only — layer them onto backendPlan so the
            // migration / weight-aware logic sees them on first load.
            if (ap.dupExerciseRm12) (backendPlan as any).dupExerciseRm12 = ap.dupExerciseRm12;
            // Iter252n — v3 DUP working-weights store must be layered
            // too (previously omitted → deload heal wrote to
            // active_plan.dupExerciseWeights but the client dropped it).
            if (ap.dupExerciseWeights) (backendPlan as any).dupExerciseWeights = ap.dupExerciseWeights;
            if (ap.apreExerciseRm) (backendPlan as any).apreExerciseRm = ap.apreExerciseRm;
          }
          return backendPlan;
        }
        
        // No training_plan in DB but active_plan exists — recover from active_plans collection
        // This handles: localStorage cleared, new device, browser cache wiped, iOS memory pressure
        if (!localPlan && response.active_plan?.dayExercises) {
          console.log('[SYNC] No local plan and no training_plan — recovering from active_plans collection');
          const ap = response.active_plan;
          // Backend stores created_at/updated_at in snake_case but frontend
          // utilities (filterHistoryForPlan, autoAdvanceIfWeekComplete) look up
          // both. Without these, a Restart-Plan followed by a logout/relogin
          // would let old training history slip past the date filter, causing
          // autoAdvanceIfWeekComplete to ratchet the new plan up to week N and
          // ultimately mark it completed → spurious "Congratulations" screen.
          const createdAtIso = ap.createdAt || ap.created_at || null;
          const updatedAtIso = ap.updatedAt || ap.updated_at || null;
          const recoveredPlan: TrainingPlan = {
            dayExercises: ap.dayExercises,
            currentWeek: ap.currentWeek || response.sync_week || 1,
            currentDayIndex: ap.currentDayIndex || 0,
            createdAt: createdAtIso,
            ...(createdAtIso && { created_at: createdAtIso } as any),
            updatedAt: updatedAtIso ? new Date(updatedAtIso).getTime() : 0,
            // Pull remaining fields from the active_plan doc
            ...(ap.trainingDays && { trainingDays: ap.trainingDays }),
            ...(ap.workoutDuration && { workoutDuration: ap.workoutDuration }),
            ...(ap.weightUnit && { weightUnit: ap.weightUnit }),
            ...(ap.bodyweight && { bodyweight: ap.bodyweight }),
            ...(ap.hasDeloadWeek !== undefined && { hasDeloadWeek: ap.hasDeloadWeek }),
            ...(ap.deloadWeek && { deloadWeek: ap.deloadWeek }),
            ...(ap.plan_id && { plan_id: ap.plan_id }),
            ...(ap.name && { name: ap.name }),
            ...(ap.planName && { planName: ap.planName } as any),
            ...(ap._preDeloadDayExercises && { _preDeloadDayExercises: ap._preDeloadDayExercises }),
            ...((ap as any).manualDeloadWeeks && { manualDeloadWeeks: (ap as any).manualDeloadWeeks }),
            ...((ap as any).manualDeloadReasons && { manualDeloadReasons: (ap as any).manualDeloadReasons }),
            // DUP-3 plan-level schedule — required so the exercise card
            // can render the `· N-rep · Hypertrophy` chip and so set 1
            // doesn't drift between 'max' / a number on cross-device
            // restore. Without these the post-refresh exercise card
            // shows a bare "DUP-3" chip because `getDupTargetRepsForDay`
            // returns null with no schedule.
            ...(ap.dupWeekdaySchedule && { dupWeekdaySchedule: ap.dupWeekdaySchedule } as any),
            ...(ap.dupRepRanges && { dupRepRanges: ap.dupRepRanges } as any),
            // Plan-level RM canonical maps (Feb 2026) — also recover.
            ...(ap.dupExerciseRm12 && { dupExerciseRm12: ap.dupExerciseRm12 } as any),
            // Iter252n — v3 DUP working-weights store (canonical since
            // Feb 2026, replaces rm12). Missing this line meant the
            // deload-heal's DUP values were dropped on fresh-device
            // recovery — user saw stale weights instead of healed ones.
            ...(ap.dupExerciseWeights && { dupExerciseWeights: ap.dupExerciseWeights } as any),
            ...(ap.apreExerciseRm && { apreExerciseRm: ap.apreExerciseRm } as any),
            // Iter275 — carry the completion flags across. Without them a
            // plan finished on device A was resurrected as an in-progress
            // week-N plan on device B (or after a cache clear), so the
            // "Congratulations / Edit & Restart Plan" screen never appeared.
            ...((ap as any).completed && { completed: true } as any),
            ...(((ap as any).completedAt || (ap as any).completed_at) && {
              completedAt: (ap as any).completedAt || (ap as any).completed_at,
            } as any),
          };
          return recoveredPlan;
        }
      } catch (err) {
        console.error('Failed to fetch plan from API:', err);
      }
    }
    
    // Fallback to localStorage
    return localPlan;
  }
  
  // Merge dayExercises - preserve local set data (reps, weights, done status)
  _mergeDayExercises(
    localDayExercises: Record<string, Exercise[]> | undefined,
    backendDayExercises: Record<string, Exercise[]> | undefined
  ): Record<string, Exercise[]> {
    if (!localDayExercises) return backendDayExercises || {};
    if (!backendDayExercises) return localDayExercises;
    
    // Start with ALL local data as base
    const merged = JSON.parse(JSON.stringify(localDayExercises)) as Record<string, Exercise[]>;
    
    // Only add days from backend that don't exist locally
    Object.keys(backendDayExercises).forEach(dayKey => {
      if (!merged[dayKey]) {
        merged[dayKey] = backendDayExercises[dayKey];
        return;
      }
      
      const localExercises = merged[dayKey];
      const backendExercises = backendDayExercises[dayKey];
      
      // If backend has more exercises, add them
      if (backendExercises.length > localExercises.length) {
        for (let i = localExercises.length; i < backendExercises.length; i++) {
          merged[dayKey].push(backendExercises[i]);
        }
      }
      
      // For each local exercise, keep local data but update structural info from backend
      merged[dayKey] = merged[dayKey].map((localExercise, exIdx) => {
        const backendExercise = backendExercises?.[exIdx];
        
        if (!backendExercise) return localExercise;
        
        // If names differ, user replaced this exercise locally — KEEP the local one
        if (localExercise.name !== backendExercise.name) {
          return localExercise;
        }
        
        return {
          ...backendExercise,
          ...localExercise,
          sets: (localExercise.sets || []).map((localSet, setIdx) => {
            const backendSet = backendExercise.sets?.[setIdx];
            if (localSet.weight !== undefined || localSet.reps !== undefined) {
              return localSet;
            }
            // Falling back to backend set — NEVER import done/skipped status
            // because the local plan may have intentionally reset them (e.g., plan copy)
            if (backendSet) {
              // Iter252bf — additively preserve local modifiers
              // (halfReps / usePartials / drops) even when we take
              // backend as the base for weight/reps. Otherwise, any
              // in-flight modifier typed before the last sync could be
              // wiped on refresh whenever this branch runs.
              const out: any = { ...backendSet, done: localSet.done || false, skipped: localSet.skipped || false };
              const lsHalf = (localSet as any).halfReps;
              if (lsHalf !== undefined && lsHalf !== '' && lsHalf !== 0 && lsHalf !== '0'
                  && (out.halfReps === undefined || out.halfReps === '' || out.halfReps === 0 || out.halfReps === '0')) {
                out.halfReps = lsHalf;
              }
              if ((localSet as any).usePartials === true && out.usePartials !== true) {
                out.usePartials = true;
              }
              const lsDrops = (localSet as any).drops;
              if (Array.isArray(lsDrops) && lsDrops.length > 0
                  && (!Array.isArray(out.drops) || out.drops.length === 0)) {
                out.drops = lsDrops;
              }
              return out;
            }
            return localSet;
          })
        };
      });
    });
    
    return merged;
  }

  async savePlan(planData: TrainingPlan): Promise<void> {
    // Always save to localStorage first. Run the plan-level RM migration
    // BEFORE persisting so the canonical maps (plan.dupExerciseRm12 /
    // plan.apreExerciseRm) are populated on every write — closes the
    // first-login race where the on-mount useEffect in useTrainingPlan
    // never sees the freshly-fetched plan.
    ensurePlanMigrated(planData);
    localStorage.setItem(STORAGE_KEYS.ACTIVE_PLAN, JSON.stringify(planData));
    
    if (this.isAuthenticated && this.syncEnabled) {
      try {
        const apiPlan = {
          name: planData.name || "Training Plan",
          workout_duration: planData.workoutDuration,
          training_weeks: planData.trainingWeeks || planData.workoutDuration,
          has_deload_week: planData.hasDeloadWeek || false,
          deload_week: planData.deloadWeek,
          training_days: planData.trainingDays,
          hiit_walking_days: planData.hiitWalkingDays || [],
          weight_unit: planData.weightUnit,
          bodyweight: planData.bodyweight || "",
          body_measurements: planData.bodyMeasurements || {},
          measurement_unit: planData.measurementUnit || "cm",
          selected_muscles: planData.selectedMuscles,
          day_muscle_assignments: planData.dayMuscleAssignments,
          day_exercises: planData.dayExercises,
          deload_exercises: planData.deloadExercises,
          current_week: planData.currentWeek || 1,
          current_day_index: planData.currentDayIndex || 0,
        };
        
        await apiFetch('/api/data/training-plan', {
          method: 'POST',
          body: JSON.stringify(apiPlan),
        });
        
        await apiFetch('/api/plans/active', {
          method: 'PUT',
          body: JSON.stringify(planData),
        });
      } catch (err) {
        console.error('Failed to save plan to API:', err);
      }
    }
  }

  async updatePlanProgress(planId: string | undefined, updates: Partial<TrainingPlan>): Promise<void> {
    // Read directly from localStorage — do NOT call getActivePlan() here
    // because it triggers a backend fetch + merge that can overwrite fresh local data
    const localSaved = localStorage.getItem(STORAGE_KEYS.ACTIVE_PLAN);
    const plan: TrainingPlan | null = localSaved ? JSON.parse(localSaved) : null;
    if (plan) {
      const updatedPlan = { ...plan, ...updates };
      ensurePlanMigrated(updatedPlan);
      localStorage.setItem(STORAGE_KEYS.ACTIVE_PLAN, JSON.stringify(updatedPlan));
    }
    
    if (this.isAuthenticated && this.syncEnabled && planId) {
      try {
        await apiFetch(`/api/data/training-plan/${planId}`, {
          method: 'PUT',
          body: JSON.stringify(updates),
        });
      } catch (err) {
        console.error('Failed to update plan progress:', err);
      }
    }
  }

  async deletePlan(planId: string | undefined): Promise<void> {
    localStorage.removeItem(STORAGE_KEYS.ACTIVE_PLAN);
    
    if (this.isAuthenticated && this.syncEnabled && planId) {
      try {
        await apiFetch(`/api/data/training-plan/${planId}`, {
          method: 'DELETE',
        });
      } catch (err) {
        console.error('Failed to delete plan:', err);
      }
    }
  }

  // ============ TRAINING HISTORY ============

  _filterOldEntries(entries: TrainingHistoryEntry[]): TrainingHistoryEntry[] {
    if (!Array.isArray(entries)) return [];
    return entries;
  }

  async getTrainingHistory(
    limit: number = 100,
    days: number = WORKOUT_HISTORY_DAYS,
    opts: { from?: string; to?: string; cache?: boolean } = {},
  ): Promise<TrainingHistoryEntry[]> {
    const { from, to, cache = true } = opts;
    if (this.isAuthenticated) {
      try {
        const qs = from || to
          ? `limit=${limit}&date_from=${from || ''}&date_to=${to || ''}`
          : `limit=${limit}&days=${days}`;
        const response = await apiFetch(`/api/data/training-history?${qs}`);
        if (response.history) {
          // Iter252bi — normalize snake_case → camelCase on read. The
          // backend stores/returns `day_key` (Mongo convention) but the
          // whole frontend filters, dedupes, and matches by `dayKey`
          // (camelCase). Without this, backend-returned entries have
          // `entry.dayKey === undefined`, so:
          //   • DataManagement's "Restore from History" filter drops
          //     them silently (validEntries requires entry.dayKey).
          //   • Exercise-card History pane finds zero same-day matches.
          //   • Weekly view can't group them by day.
          // Symptom: recent workouts logged today (which came back
          // from the backend on next load) disappear from every
          // history surface until the localStorage copy of them —
          // written in camelCase by the client — is loaded. That's
          // the root of Verner's "14.08 / 15.08 not shown" report.
          const normalized = (response.history as any[]).map((e: any) => {
            if (!e || typeof e !== 'object') return e;
            const out: any = { ...e };
            if (out.dayKey === undefined && out.day_key !== undefined) {
              out.dayKey = out.day_key;
            }
            if (out.durationMinutes === undefined && out.duration_minutes !== undefined) {
              out.durationMinutes = out.duration_minutes;
            }
            return out;
          });
          const filtered = this._filterOldEntries(normalized);
          // A windowed read (Calendar month paging, all-time Statistics) must
          // NOT overwrite the local snapshot — that cache is the offline copy
          // of the CURRENT window and is read by the plan loader.
          if (cache) {
            localStorage.setItem(STORAGE_KEYS.TRAINING_HISTORY, JSON.stringify(filtered));
          }
          return filtered;
        }
      } catch (err) {
        console.error('Failed to fetch training history:', err);
      }
    }
    
    const saved = localStorage.getItem(STORAGE_KEYS.TRAINING_HISTORY);
    const history: TrainingHistoryEntry[] = saved ? JSON.parse(saved) : [];
    const local = this._filterOldEntries(history);
    if (!from && !to) return local;
    return local.filter((e: any) => {
      const d = String(e?.date || '').slice(0, 10);
      return (!from || d >= from) && (!to || d <= to);
    });
  }

  async addTrainingHistory(entry: TrainingHistoryEntry): Promise<SyncResult> {
    const history = await this.getTrainingHistory();
    const updatedHistory = [entry, ...history].slice(0, 100);
    localStorage.setItem(STORAGE_KEYS.TRAINING_HISTORY, JSON.stringify(updatedHistory));
    
    if (this.isAuthenticated) {
      try {
        const response = await apiFetch('/api/data/training-history', {
          method: 'POST',
          body: JSON.stringify({
            date: entry.date,
            day_key: entry.dayKey,
            day: entry.day,
            week: entry.week,
            skipped: entry.skipped || false,
            exercises: entry.exercises,
            duration_minutes: entry.durationMinutes,
            notes: entry.notes || "",
            // Iter362 — which cycle this session belongs to. Without it, the
            // previous plan's final-week rows could be mistaken for the
            // current plan's and complete a fresh cycle on day two.
            plan_id: (entry as any).planId || null,
          }),
        });
        console.log('[DataService] Training history synced to backend:', response);
        notifyDataChanged();
        
        // Update localStorage entry with backend entry_id for proper sync tracking
        if (response && (response as any).entry_id) {
          const currentHistory = JSON.parse(localStorage.getItem(STORAGE_KEYS.TRAINING_HISTORY) || '[]');
          const idx = currentHistory.findIndex((h: any) =>
            h.date === entry.date && (h.dayKey === entry.dayKey || h.day_key === entry.dayKey) && h.week === entry.week
          );
          if (idx >= 0) {
            currentHistory[idx].entry_id = (response as any).entry_id;
            localStorage.setItem(STORAGE_KEYS.TRAINING_HISTORY, JSON.stringify(currentHistory));
          }
        }
        
        return { synced: true, response };
      } catch (err) {
        const error = err as Error;
        console.error('[DataService] SYNC FAILED - Training history not saved to cloud:', error);
        return { synced: false, error: error.message || 'Sync failed' };
      }
    }
    return { synced: false, reason: 'not_authenticated' };
  }

  async deleteTrainingHistoryEntry(entryId: string | undefined, localId: string | undefined, entry?: any): Promise<TrainingHistoryEntry[]> {
    const history = await this.getTrainingHistory();

    // Find the target entry first (for fallback date-based delete)
    const targetEntry = entry || history.find(h =>
      (entryId && h.entry_id === entryId) ||
      (localId && (h.id === localId || String(h.id) === String(localId)))
    );

    const updatedHistory = history.filter(h => {
      const matchesEntryId = entryId && h.entry_id === entryId;
      const matchesLocalId = localId && (h.id === localId || String(h.id) === String(localId));
      return !matchesEntryId && !matchesLocalId;
    });
    localStorage.setItem(STORAGE_KEYS.TRAINING_HISTORY, JSON.stringify(updatedHistory));
    
    if (this.isAuthenticated) {
      try {
        if (entryId) {
          // Delete by entry_id (covers training_history collection + sync_data)
          await apiFetch(`/api/data/training-history/${entryId}`, { method: 'DELETE' });
        } else if (targetEntry) {
          // Fallback: delete by date+dayKey+week (for entries without entry_id)
          await apiFetch('/api/data/training-history-by-date', {
            method: 'DELETE',
            body: JSON.stringify({
              date: targetEntry.date,
              dayKey: targetEntry.dayKey || targetEntry.day_key,
              week: targetEntry.week,
            }),
          });
        }
      } catch (err) {
        console.error('Failed to delete from backend:', err);
      }
    }
    
    return updatedHistory;
  }

  async clearTrainingHistory(): Promise<void> {
    localStorage.removeItem(STORAGE_KEYS.TRAINING_HISTORY);
    
    if (this.isAuthenticated && this.syncEnabled) {
      try {
        await apiFetch('/api/data/training-history', { method: 'DELETE' });
      } catch (err) {
        console.error('Failed to clear history from backend:', err);
      }
    }
  }

  // ============ EXERCISE HISTORY (Personal Records) ============

  async getExerciseHistory(): Promise<ExerciseHistoryMap> {
    if (this.isAuthenticated && this.syncEnabled) {
      try {
        const response = await apiFetch('/api/data/exercise-history');
        // Backend returns `exercise_history` as the primary key (with
        // `history` as a legacy alias). Prefer `exercise_history` so
        // reconstruction-path payloads land correctly — the older code
        // that only checked `response.history` silently missed the
        // primary payload and fell back to localStorage, which is what
        // caused dropsets to look missing on production.
        const payload = response.exercise_history || response.history;
        if (payload) {
          const filtered = this._filterExerciseHistoryObject(payload);
          // SAFE MERGE with local: preserve `drops` / `halfReps` /
          // `usePartials` that live only on the client (older backend
          // reconstructions strip them). Never let a "clean" backend
          // payload wipe out drop chains the user actually logged and
          // persisted locally.
          const merged = this._mergeExerciseHistoryPreservingDrops(filtered);
          localStorage.setItem(STORAGE_KEYS.EXERCISE_HISTORY, JSON.stringify(merged));
          clearExerciseHistoryCache();
          return merged;
        }
      } catch (err) {
        console.error('Failed to fetch exercise history:', err);
      }
    }
    
    const saved = localStorage.getItem(STORAGE_KEYS.EXERCISE_HISTORY);
    const history: ExerciseHistoryMap = saved ? JSON.parse(saved) : {};
    return this._filterExerciseHistoryObject(history);
  }

  /** Merge a fresh backend exercise_history payload with the current
   *  localStorage copy, preferring the backend for canonical fields
   *  (weight / reps / weightUnit / week) but keeping the local
   *  `drops` chain, `halfReps` count, and `usePartials` flag whenever
   *  the backend entry is missing them. This prevents cross-device or
   *  post-migration hydrations from silently wiping dropset history
   *  that the client persisted before the write-path was fixed. */
  private _mergeExerciseHistoryPreservingDrops(backend: ExerciseHistoryMap): ExerciseHistoryMap {
    let local: ExerciseHistoryMap = {};
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.EXERCISE_HISTORY);
      local = raw ? JSON.parse(raw) : {};
    } catch { local = {}; }
    if (!local || typeof local !== 'object') return backend;

    const out: ExerciseHistoryMap = { ...backend };
    for (const exName of Object.keys(backend)) {
      const bEntries = Array.isArray(backend[exName]) ? backend[exName] : [];
      const lEntries = Array.isArray(local[exName]) ? local[exName] : [];
      if (!lEntries.length) continue;
      out[exName] = bEntries.map((bEntry: any) => {
        if (!bEntry || typeof bEntry !== 'object') return bEntry;
        const bYmd = (bEntry.date || '').slice(0, 10);
        const lEntry = lEntries.find((l: any) => (l?.date || '').slice(0, 10) === bYmd);
        if (!lEntry || !Array.isArray(lEntry.sets)) return bEntry;
        const bSets: any[] = Array.isArray(bEntry.sets) ? bEntry.sets : [];
        return {
          ...bEntry,
          sets: bSets.map((bs: any, i: number) => {
            const ls: any = lEntry.sets[i];
            if (!ls || typeof ls !== 'object') return bs;
            const enriched: any = { ...bs };
            if ((!Array.isArray(enriched.drops) || enriched.drops.length === 0)
                && Array.isArray(ls.drops) && ls.drops.length > 0) {
              enriched.drops = ls.drops;
            }
            if ((enriched.halfReps === undefined || enriched.halfReps === 0 || enriched.halfReps === '0' || enriched.halfReps === '')
                && ls.halfReps !== undefined && ls.halfReps !== 0 && ls.halfReps !== '0' && ls.halfReps !== '') {
              enriched.halfReps = ls.halfReps;
            }
            if (enriched.usePartials === undefined && ls.usePartials !== undefined) {
              enriched.usePartials = ls.usePartials;
            }
            return enriched;
          }),
        };
      });
    }
    return out;
  }

  async updateExerciseHistory(exercises: ExerciseHistoryMap): Promise<void> {
    // Route the localStorage write through the in-memory cache so the
    // hot-path debounce in TrainingInProgress isn't defeated by a second
    // immediate write (iter182). replaceExerciseHistory cancels any pending
    // debounced write, updates the cache, and does one synchronous setItem.
    writeExerciseHistoryCache(exercises);
    
    if (this.isAuthenticated && this.syncEnabled) {
      try {
        await apiFetch('/api/data/exercise-history', {
          method: 'PUT',
          body: JSON.stringify(exercises),
        });
      } catch (err) {
        console.error('Failed to update exercise history:', err);
      }
    }
  }

  // ============ FAVORITES ============

  _convertFavoriteFromBackend(backendFavorite: Favorite): Favorite {
    return {
      id: backendFavorite.favorite_id || backendFavorite.id,
      favorite_id: backendFavorite.favorite_id,
      name: backendFavorite.name,
      workoutDuration: backendFavorite.workout_duration || backendFavorite.workoutDuration,
      trainingDays: backendFavorite.training_days || backendFavorite.trainingDays,
      hiitWalkingDays: backendFavorite.hiit_walking_days || backendFavorite.hiitWalkingDays || [],
      weightUnit: backendFavorite.weight_unit || backendFavorite.weightUnit,
      bodyweight: backendFavorite.bodyweight,
      bodyMeasurements: backendFavorite.body_measurements || backendFavorite.bodyMeasurements || {},
      measurementUnit: backendFavorite.measurement_unit || backendFavorite.measurementUnit || 'cm',
      selectedMuscles: backendFavorite.selected_muscles || backendFavorite.selectedMuscles,
      dayMuscleAssignments: backendFavorite.day_muscle_assignments || backendFavorite.dayMuscleAssignments,
      dayExercises: backendFavorite.day_exercises || backendFavorite.dayExercises,
      createdAt: backendFavorite.created_at || backendFavorite.createdAt,
    };
  }

  async getFavorites(): Promise<Favorite[]> {
    if (this.isAuthenticated && this.syncEnabled) {
      try {
        const response = await apiFetch('/api/data/favorites');
        if (response.favorites && response.favorites.length > 0) {
          const convertedFavorites = response.favorites.map(f => this._convertFavoriteFromBackend(f));
          // Merge: backend favorites + any local-only favorites not yet synced
          const localSaved = localStorage.getItem(STORAGE_KEYS.FAVORITES);
          const localFavorites: Favorite[] = localSaved ? JSON.parse(localSaved) : [];
          const backendIds = new Set(convertedFavorites.map(f => String(f.id)));
          const backendFavIds = new Set(convertedFavorites.map(f => f.favorite_id).filter(Boolean));
          const localOnly = localFavorites.filter(f => 
            !backendIds.has(String(f.id)) && !backendFavIds.has(f.favorite_id)
          );
          const merged = [...convertedFavorites, ...localOnly];
          localStorage.setItem(STORAGE_KEYS.FAVORITES, JSON.stringify(merged));
          return merged;
        }
        // Backend returned empty list — DON'T overwrite localStorage
        // Local favorites may exist that haven't been synced yet
      } catch (err) {
        console.error('Failed to fetch favorites:', err);
      }
    }
    
    const saved = localStorage.getItem(STORAGE_KEYS.FAVORITES);
    return saved ? JSON.parse(saved) : [];
  }

  async addFavorite(favorite: Favorite): Promise<Favorite[]> {
    const favorites = await this.getFavorites();
    const newFavorite: Favorite = {
      id: favorite.id || Date.now(),
      ...favorite,
      createdAt: favorite.createdAt || new Date().toISOString(),
    };
    const updatedFavorites = [newFavorite, ...favorites];
    localStorage.setItem(STORAGE_KEYS.FAVORITES, JSON.stringify(updatedFavorites));
    
    if (this.isAuthenticated && this.syncEnabled) {
      try {
        await apiFetch('/api/data/favorites', {
          method: 'POST',
          body: JSON.stringify({
            name: favorite.name,
            workout_duration: favorite.workoutDuration,
            training_days: favorite.trainingDays,
            hiit_walking_days: favorite.hiitWalkingDays || [],
            weight_unit: favorite.weightUnit,
            bodyweight: favorite.bodyweight || "",
            body_measurements: favorite.bodyMeasurements || {},
            measurement_unit: favorite.measurementUnit || "cm",
            selected_muscles: favorite.selectedMuscles,
            day_muscle_assignments: favorite.dayMuscleAssignments,
            day_exercises: favorite.dayExercises,
          }),
        });
      } catch (err) {
        console.error('Failed to add favorite:', err);
      }
    }
    
    return updatedFavorites;
  }

  async deleteFavorite(favoriteId: string | number): Promise<Favorite[]> {
    const favorites = await this.getFavorites();
    const updatedFavorites = favorites.filter(f => f.id !== favoriteId && f.favorite_id !== favoriteId);
    localStorage.setItem(STORAGE_KEYS.FAVORITES, JSON.stringify(updatedFavorites));
    
    if (this.isAuthenticated && this.syncEnabled) {
      try {
        await apiFetch(`/api/data/favorites/${favoriteId}`, {
          method: 'DELETE',
        });
      } catch (err) {
        console.error('Failed to delete favorite:', err);
      }
    }
    
    return updatedFavorites;
  }

  // ============ BODY MEASUREMENTS ============

  async getBodyMeasurements(limit: number = 50): Promise<BodyMeasurementEntry[]> {
    if (this.isAuthenticated && this.syncEnabled) {
      try {
        const response = await apiFetch(`/api/data/body-measurements?limit=${limit}`);
        if (response.measurements) {
          localStorage.setItem(STORAGE_KEYS.BODY_MEASUREMENTS, JSON.stringify(response.measurements));
          return response.measurements;
        }
      } catch (err) {
        console.error('Failed to fetch body measurements:', err);
      }
    }
    
    const saved = localStorage.getItem(STORAGE_KEYS.BODY_MEASUREMENTS);
    return saved ? JSON.parse(saved) : [];
  }

  async addBodyMeasurement(entry: BodyMeasurementEntry): Promise<void> {
    const measurements = await this.getBodyMeasurements();
    const updatedMeasurements = [entry, ...measurements].slice(0, 50);
    localStorage.setItem(STORAGE_KEYS.BODY_MEASUREMENTS, JSON.stringify(updatedMeasurements));
    
    if (this.isAuthenticated && this.syncEnabled) {
      try {
        await apiFetch('/api/data/body-measurements', {
          method: 'POST',
          body: JSON.stringify({
            date: entry.date,
            bodyweight: entry.bodyweight || "",
            weight_unit: entry.weightUnit,
            measurements: entry.measurements,
            measurement_unit: entry.measurementUnit,
          }),
        });
        notifyDataChanged();
      } catch (err) {
        console.error('Failed to add body measurement:', err);
      }
    }
  }

  // ============ PROGRESS PHOTOS ============

  async getProgressPhotos(): Promise<ProgressPhoto[]> {
    const saved = localStorage.getItem(STORAGE_KEYS.PROGRESS_PHOTOS);
    return saved ? JSON.parse(saved) : [];
  }

  async saveProgressPhotos(photos: ProgressPhoto[]): Promise<void> {
    localStorage.setItem(STORAGE_KEYS.PROGRESS_PHOTOS, JSON.stringify(photos));
  }

  // ============ SETTINGS ============

  getTheme(): string {
    return localStorage.getItem(STORAGE_KEYS.THEME) || 'midnight';
  }

  setTheme(theme: string): void {
    localStorage.setItem(STORAGE_KEYS.THEME, theme);
  }

  getFocusMode(): boolean {
    return localStorage.getItem(STORAGE_KEYS.FOCUS_MODE) === 'true';
  }

  setFocusMode(enabled: boolean): void {
    localStorage.setItem(STORAGE_KEYS.FOCUS_MODE, enabled.toString());
  }

  // ============ SYNC ALL DATA ============

  async syncAllToCloud(): Promise<unknown> {
    if (!this.isAuthenticated || !this.syncEnabled) {
      throw new Error('Sync not available');
    }

    // Defensive shape coercion — see SyncContext.tsx for rationale.
    // If a localStorage key was written with the wrong shape by an older
    // build or by a runtime bug, we do not want /api/sync/upload to 422.
    const readArr = (k: string) => { try { const v = JSON.parse(localStorage.getItem(k) || '[]'); return Array.isArray(v) ? v : []; } catch { return []; } };
    const readObj = (k: string) => { try { const v = JSON.parse(localStorage.getItem(k) || '{}'); return (v && typeof v === 'object' && !Array.isArray(v)) ? v : {}; } catch { return {}; } };
    const readObjOrNull = (k: string) => { try { const raw = localStorage.getItem(k); if (!raw) return null; const v = JSON.parse(raw); return (v && typeof v === 'object' && !Array.isArray(v)) ? v : null; } catch { return null; } };

    const syncData: SyncData = {
      data_type: 'full',
      training_plan: readObjOrNull(STORAGE_KEYS.ACTIVE_PLAN),
      training_history: readArr(STORAGE_KEYS.TRAINING_HISTORY),
      exercise_history: readObj(STORAGE_KEYS.EXERCISE_HISTORY),
      favorites: readArr(STORAGE_KEYS.FAVORITES),
      body_measurements: readArr(STORAGE_KEYS.BODY_MEASUREMENTS),
      progress_photos: readArr(STORAGE_KEYS.PROGRESS_PHOTOS),
      settings: {
        theme: this.getTheme(),
      },
      // Extended data types
      custom_exercises: readArr('gym_notebook_custom_exercises'),
      saved_meals: readArr('gym_notebook_saved_meals'),
      nutrition_days: readArr('gym_notebook_nutrition_days'),
      walking_sessions: readArr('gym_notebook_walking_sessions'),
      custom_products: readArr('gym_notebook_custom_products'),
    };

    return apiFetch('/api/sync/upload', { method: 'POST', body: JSON.stringify(syncData) });
  }

  async syncAllFromCloud(): Promise<unknown> {
    if (!this.isAuthenticated || !this.syncEnabled) {
      throw new Error('Sync not available');
    }

    interface CloudData {
      training_plan?: TrainingPlan;
      training_history?: TrainingHistoryEntry[];
      exercise_history?: ExerciseHistoryMap;
      favorites?: Favorite[];
      body_measurements?: BodyMeasurementEntry[];
      progress_photos?: ProgressPhoto[];
      settings?: { theme?: string };
    }

    const response = await apiFetch('/api/sync/download') as { data?: CloudData };
    if (response.data) {
      const data = response.data;
      
      if (data.training_plan) {
        ensurePlanMigrated(data.training_plan);
        localStorage.setItem(STORAGE_KEYS.ACTIVE_PLAN, JSON.stringify(data.training_plan));
      }
      if (data.training_history) {
        const cleanedHistory = this._filterOldEntries(data.training_history);
        localStorage.setItem(STORAGE_KEYS.TRAINING_HISTORY, JSON.stringify(cleanedHistory));
      }
      if (data.exercise_history) {
        const cleanedExerciseHistory = this._filterExerciseHistoryObject(data.exercise_history);
        localStorage.setItem(STORAGE_KEYS.EXERCISE_HISTORY, JSON.stringify(cleanedExerciseHistory));
        // Invalidate the in-memory cache so a subsequent read re-parses
        // the fresh backend-merged data instead of stale in-tab state.
        clearExerciseHistoryCache();
      }
      if (data.favorites) {
        localStorage.setItem(STORAGE_KEYS.FAVORITES, JSON.stringify(data.favorites));
      }
      if (data.body_measurements) {
        localStorage.setItem(STORAGE_KEYS.BODY_MEASUREMENTS, JSON.stringify(data.body_measurements));
      }
      if (data.progress_photos) {
        localStorage.setItem(STORAGE_KEYS.PROGRESS_PHOTOS, JSON.stringify(data.progress_photos));
      }
      if (data.settings?.theme) {
        this.setTheme(data.settings.theme);
      }
    }
    
    return response;
  }

  _filterExerciseHistoryObject(exerciseHistory: ExerciseHistoryMap | unknown): ExerciseHistoryMap {
    if (!exerciseHistory || typeof exerciseHistory !== 'object' || Array.isArray(exerciseHistory)) {
      return {};
    }
    
    const cutoffDate = new Date('2025-01-13T00:00:00Z').getTime();
    const cleaned: ExerciseHistoryMap = {};
    const historyObj = exerciseHistory as ExerciseHistoryMap;
    
    Object.keys(historyObj).forEach(exerciseName => {
      const entries = historyObj[exerciseName];
      if (Array.isArray(entries)) {
        cleaned[exerciseName] = entries.filter(entry => {
          const dateStr = entry.date || '';
          if (dateStr.includes('2025-01-11') || dateStr.includes('2025-01-12') ||
              dateStr.includes('2026-01-11') || dateStr.includes('2026-01-12')) {
            console.log('[DataService] Filtering out old exercise entry:', exerciseName, dateStr);
            return false;
          }
          const entryDate = new Date(dateStr).getTime();
          return isNaN(entryDate) || entryDate >= cutoffDate;
        });
      }
    });
    
    return cleaned;
  }

  async deleteActivePlan(): Promise<void> {
    if (!this.isAuthenticated) return;
    
    try {
      await apiFetch('/api/plans/active', { method: 'DELETE' });
      
      const activePlan = await this.getActivePlan();
      if (activePlan?.plan_id) {
        try {
          await apiFetch(`/api/data/training-plan/${activePlan.plan_id}`, { method: 'DELETE' });
        } catch {
          // Ignore if this fails - main deletion already done
        }
      }
    } catch (error) {
      console.error('Error deleting active plan:', error);
    }
  }

  async deleteAllTrainingHistory(): Promise<void> {
    if (!this.isAuthenticated) return;
    
    try {
      await apiFetch('/api/data/training-history', { method: 'DELETE' });
      await apiFetch('/api/data/exercise-history', { method: 'DELETE' });
    } catch (error) {
      console.error('Error deleting training history:', error);
    }
  }

  async deleteBodyMeasurements(): Promise<void> {
    if (!this.isAuthenticated) return;
    
    try {
      await apiFetch('/api/data/body-measurements', { method: 'DELETE' });
    } catch (error) {
      console.error('Error deleting body measurements:', error);
    }
  }
}

export const dataService = new DataService();
export default dataService;
